import React, { Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Redirect } from "../auth/redirect";
import { ChangePassword } from "../components/authentication/change-password";
import { ConfirmEmail } from "../components/authentication/confirm-email";
import { EmailConfirmation } from "../components/authentication/email-confirmation";
import { ForgotPassword } from "../components/authentication/forgot-password";
import { Invitation } from "../components/authentication/invitation";
import { Login } from "../components/authentication/login";
import { PasswordChangeSuccess } from "../components/authentication/password-change-success";
import { ResetPassword } from "../components/authentication/reset-password";
import { NoMatch404 } from "../components/common/no-match-404";
import UnsupportedDevicePage from "../components/common/unsupported-device-page";
import { HelpdeskEntryRedirect, HelpdeskPage } from "../components/design-tools/helpdesk/helpdesk-page";
import { ScreenDesignRoute } from "../components/design-tools/screen-design-route";
import { KuikaSpinner } from "../components/design-tools/spinner/kuika-spinner";
import { DomainAccessRequiredModal } from "../components/domain-access/domain-access-required-modal";
import { Board } from "../components/management/board/board-page";
import { BoardProjectHub } from "../components/management/board/board-project-hub";
import { BoardProjectsIndex } from "../components/management/board/board-projects-index";
import { IssueFullPage } from "../components/management/board/issue-full-page";
import { CloudDeployments } from "../components/management/cloud/deployments/cloud-deployments";
import { Deployment } from "../components/management/cloud/deployments/deployment";
import { DeploymentDetails } from "../components/management/cloud/deployments/deployment-details";
import { CloudDomains } from "../components/management/cloud/domains/cloud-domains";
import { DomainDetails } from "../components/management/cloud/domains/domain-details";
import { GlobalConfigsCanvas } from "../components/management/global-configs/global-configs-canvas";
import { EProjectListType, ProjectListBase } from "../components/management/projects/project-list/project-list-base";
import AiChatbotAdminPanel from "../components/management/settings/ai-chatbot/ai-chatbot-admin-panel";
import AiChatbotPanel from "../components/management/settings/ai-chatbot/ai-chatbot-panel";
import { Billing } from "../components/management/settings/billing/billing";
import { ComponentDefList } from "../components/management/settings/component-set/component-def-list";
import { ComponentSet } from "../components/management/settings/component-set/component-set";
import OnboardingPanel from "../components/management/settings/onboarding/onboarding-panel";
import { SubscriptionChange } from "../components/management/settings/subscriptions/subscription-change";
import { SubscriptionPlans } from "../components/management/settings/subscriptions/subscription-plans";
const UiKitLab = React.lazy(() =>
  import("../components/management/settings/ui-kit-showcase/ui-kit-lab").then((module) => ({
    default: module.UiKitLab
  }))
);
import { Roles } from "../components/management/settings/roles/roles";
import { Users } from "../components/management/settings/users/users";
import GlobalOnboardingTooltipLayer from "../components/onboarding-tooltip/global-onboarding-tooltip-layer";
import { GlobalOnboardingTooltipManager } from "../components/onboarding-tooltip/global-onboarding-tooltip-manager";
import { RouteChangeTooltipListener } from "../components/onboarding-tooltip/route-change-tooltip-listener";
import { TitleManager } from "../shared/utility/title-manager";
import PrivateRoutes from "./private-routes";

export interface IRouterState {}

export interface IRouterProps {}

declare let window: any;

export class Router extends React.PureComponent<IRouterProps, IRouterState> {
  componentDidMount() {
    TitleManager.updateTitle();
  }

  render() {
    return (
      <BrowserRouter>
        <DomainAccessRequiredModal />
        <GlobalOnboardingTooltipManager />
        <GlobalOnboardingTooltipLayer />
        <RouteChangeTooltipListener />
        <Suspense fallback={<KuikaSpinner />}>
          <Routes>
            <Route path="/" element={<Redirect />} />
            <Route path="/redirect" element={<Redirect />} />
            <Route path="/unsupported-device" element={<UnsupportedDevicePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route path="/changepassword" element={<ChangePassword />} />
            <Route path="/passwordchangesuccess" element={<PasswordChangeSuccess />} />
            <Route path="/resetpassword" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/invitation" element={<Invitation />} />
            <Route path="/email-confirmation" element={<EmailConfirmation />} />
            <Route element={<PrivateRoutes />}>
              <Route path="/helpdesk" element={<HelpdeskEntryRedirect />} />
              <Route path="/:domainName/helpdesk" element={<HelpdeskPage />} />
              <Route path="/:domainName/users" element={<Users />} />
              <Route
                path="/:domainName/projects"
                element={<ProjectListBase projectListType={EProjectListType.Recent} />}
              />
              <Route path="/:domainName/board" element={<BoardProjectsIndex />} />
              <Route path="/:domainName/board/multi/:boardSlug" element={<Board />} />
              <Route path="/:domainName/board/multi/:boardSlug/:issueKey" element={<Board />} />
              <Route path="/:domainName/board/:projectKey" element={<BoardProjectHub />} />
              <Route path="/:domainName/board/:projectKey/:boardSlug" element={<Board />} />
              <Route path="/:domainName/board/:projectKey/:boardSlug/:issueKey" element={<Board />} />
              <Route path="/:domainName/issue/:issueKey" element={<IssueFullPage />} />
              <Route
                path="/:domainName/projects/favorites"
                element={<ProjectListBase projectListType={EProjectListType.Favorites} />}
              />
              <Route
                path="/:domainName/projects/recent"
                element={<ProjectListBase projectListType={EProjectListType.Recent} />}
              />
              <Route
                path="/:domainName/projects/myprojects"
                element={<ProjectListBase projectListType={EProjectListType.MyProjects} />}
              />
              <Route
                path="/:domainName/projects/allprojects"
                element={<ProjectListBase projectListType={EProjectListType.All} />}
              />
              <Route
                path="/:domainName/projects/archive"
                element={<ProjectListBase projectListType={EProjectListType.Archive} />}
              />
              <Route
                path="/:domainName/projects/templates"
                element={<ProjectListBase projectListType={EProjectListType.Templates} />}
              />
              <Route path="/:domainName/settings" element={<ComponentSet />} />
              <Route path="/:domainName/settings/componentset" element={<ComponentSet />} />
              <Route path="/:domainName/settings/users" element={<Users />} />
              <Route path="/:domainName/settings/roles" element={<Roles />} />
              <Route path="/:domainName/settings/billing" element={<Billing />} />
              <Route path="/:domainName/settings/onboarding" element={<OnboardingPanel />} />
              <Route path="/:domainName/settings/ai-chatbot-credits" element={<AiChatbotPanel />} />
              <Route path="/:domainName/settings/ai-chatbot-admin" element={<AiChatbotAdminPanel />} />
              <Route path="/:domainName/settings/componentlist" element={<ComponentDefList />} />
              <Route path="/:domainName/settings/componentlist/:csId" element={<ComponentDefList />} />
              <Route path="/:domainName/settings/subscriptionPlans/*" element={<SubscriptionPlans />} />
              <Route path="/:domainName/settings/subscriptionChange/:aId" element={<SubscriptionChange />} />
              <Route path="/:domainName/sd/:sdId" element={<ScreenDesignRoute />} />
              <Route path="/:domainName/globalConfigurations" element={<GlobalConfigsCanvas />} />

              <Route path="/:domainName/ui-kit" element={<UiKitLab />} />
              <Route path="/:domainName/ui-kit/:slug" element={<UiKitLab />} />
              <Route path="/:domainName/cloud/domains" element={<CloudDomains />} />
              <Route path="/:domainName/cloud/domains/:appName" element={<DomainDetails />} />
              <Route path="/:domainName/cloud/deployments" element={<CloudDeployments />} />
              <Route path="/:domainName/cloud/deployments/:appName" element={<DeploymentDetails />} />
              <Route path="/:domainName/cloud/deployments/:appName/production/:buildNumber" element={<Deployment />} />
            </Route>
            <Route path="*" element={<NoMatch404 />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }
}
